import { useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import { useNova } from '../../lib/store';
import { Button } from '../ui';
import { AddButton, Cell, ConfirmButton, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, Toggle, useLoad } from './kit';

interface Widget {
  id: string;
  name: string;
  public_key: string;
  allowed_domains: string[];
  queue_id: string | null;
  queue_name: string | null;
  business_hours_id: string | null;
  greeting: string;
  offline_message: string;
  color: string;
  active: boolean;
}
interface Named {
  id: string;
  name: string;
}

/** The line a website pastes to show the chat bubble. */
const snippet = (key: string, color: string) =>
  `<script src="${location.origin}/widget.js" data-key="${key}" data-color="${color}" async></script>`;

/**
 * Livechat widgets: what a website embeds so visitors can chat. Each has
 * the websites allowed to show it, the chat queue its chats go to, opening
 * hours, and its texts and colour.
 */
export function Livechat() {
  const [widgets, reload] = useLoad<Widget[]>('/chat-widgets');
  const [editing, setEditing] = useState<Widget | 'new' | null>(null);
  return (
    <>
      <SectionHeader
        title="Livechat"
        lead="A chat bubble for your websites. Visitors chat with agents who have room under their own chat limit (set per user). Only the websites you list can show it."
        action={<AddButton onClick={() => setEditing('new')}>New widget</AddButton>}
      />
      <Table head={['Widget', 'Chats go to', 'Websites', 'Status']} empty={widgets?.length === 0 && 'No widgets yet. Add one to get the code for your website.'}>
        {(widgets ?? []).map((w) => (
          <Row key={w.id} onClick={() => setEditing(w)}>
            <Cell>
              <span className="flex items-center gap-2 font-medium">
                <span className="h-3 w-3 rounded" style={{ background: w.color }} aria-hidden />
                {w.name}
              </span>
            </Cell>
            <Cell>{w.queue_name ?? <span className="text-break">No queue: chats cannot start</span>}</Cell>
            <Cell className="text-ink-2">{w.allowed_domains.length ? w.allowed_domains.join(', ') : 'Only the test page'}</Cell>
            <Cell>{w.active ? <Pill tone="good">On</Pill> : <Pill>Off</Pill>}</Cell>
          </Row>
        ))}
      </Table>
      {editing ? (
        <WidgetDrawer
          widget={editing === 'new' ? null : editing}
          onDone={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function WidgetDrawer({ widget, onDone }: { widget: Widget | null; onDone: () => void }) {
  const queues = useNova((s) => s.queueList) as (Named & { channel?: string })[];
  const [allQueues] = useLoad<(Named & { channel: string })[]>('/queues');
  const [hours] = useLoad<Named[]>('/business-hours');
  const chatQueues = (allQueues ?? queues).filter((q) => q.channel === 'chat');
  const [f, setF] = useState({
    name: widget?.name ?? 'Website chat',
    queueId: widget?.queue_id ?? '',
    businessHoursId: widget?.business_hours_id ?? '',
    sites: (widget?.allowed_domains ?? []).join('\n'),
    greeting: widget?.greeting ?? 'Hi! How can we help you today?',
    offlineMessage: widget?.offline_message ?? 'We are closed right now. Please come back during our working hours.',
    color: widget?.color ?? '#0f6b4d',
    active: widget?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const sites = f.sites
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  async function save() {
    setBusy(true);
    const body = {
      name: f.name,
      queueId: f.queueId || null,
      businessHoursId: f.businessHoursId || null,
      allowedDomains: sites,
      greeting: f.greeting,
      offlineMessage: f.offlineMessage,
      color: f.color,
      active: f.active,
    };
    const ok = await act(() => (widget ? api('PATCH', `/chat-widgets/${widget.id}`, body) : api('POST', '/chat-widgets', body)), widget ? 'Widget saved' : 'Widget added');
    setBusy(false);
    if (ok) onDone();
  }
  async function remove() {
    const ok = await act(() => api('DELETE', `/chat-widgets/${widget!.id}`).then(() => true), 'Widget deleted');
    if (ok) onDone();
  }

  return (
    <Drawer
      title={widget ? widget.name : 'New livechat widget'}
      open
      onClose={onDone}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" disabled={busy || f.name.trim().length < 2} onClick={() => void save()}>
            {widget ? 'Save' : 'Add widget'}
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onDone}>
            Cancel
          </Button>
          {widget ? (
            <span className="ml-auto">
              <ConfirmButton confirmText="Delete it" onConfirm={() => void remove()}>
                Delete
              </ConfirmButton>
            </span>
          ) : null}
        </>
      }
    >
      {widget ? (
        <div className="mb-5 rounded-lg border border-line p-3">
          <p className="mb-1 text-sm font-medium">Add it to your website</p>
          <p className="mb-2 text-xs text-ink-3">Paste this just before &lt;/body&gt; on every page that should show the chat bubble.</p>
          <code className="block break-all rounded bg-abyss px-2 py-1.5 text-[11px]">{snippet(widget.public_key, widget.color)}</code>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              tone="quiet"
              className="px-3 py-1.5 text-sm"
              onClick={() => {
                void navigator.clipboard?.writeText(snippet(widget.public_key, widget.color)).then(() => setCopied(true));
              }}
            >
              {copied ? 'Copied' : 'Copy code'}
            </Button>
            <a className="rounded-lg border border-line px-3 py-1.5 text-sm hover:border-line-strong" href={`/chat-test.html?key=${encodeURIComponent(widget.public_key)}`} target="_blank" rel="noreferrer">
              Open test page
            </a>
          </div>
        </div>
      ) : null}
      <Field label="Name" hint="Shown at the top of the chat window">
        {(id) => <input id={id} className={inputClass} value={f.name} onChange={(e) => set('name', e.target.value)} />}
      </Field>
      <Field label="Chats go to" hint={chatQueues.length ? 'A livechat queue. Its skills decide who gets the chats.' : 'Add a queue with channel Livechat under Queues first.'}>
        {(id) => (
          <select id={id} className={inputClass} value={f.queueId} onChange={(e) => set('queueId', e.target.value)}>
            <option value="">Choose a livechat queue</option>
            {chatQueues.map((q) => (
              <option key={q.id} value={q.id}>
                {q.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Websites allowed to show it" hint="One per line, like www.test.com or *.test.com. Nova's own test page always works.">
        {(id) => <textarea id={id} rows={3} className={inputClass} value={f.sites} onChange={(e) => set('sites', e.target.value)} placeholder="www.test.com" />}
      </Field>
      <Field label="Open hours" hint="Outside them, visitors see the closed message instead of the chat. Empty: the queue's hours.">
        {(id) => (
          <select id={id} className={inputClass} value={f.businessHoursId} onChange={(e) => set('businessHoursId', e.target.value)}>
            <option value="">The queue's hours</option>
            {(hours ?? []).map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Greeting">{(id) => <input id={id} className={inputClass} value={f.greeting} onChange={(e) => set('greeting', e.target.value)} />}</Field>
      <Field label="When closed">{(id) => <textarea id={id} rows={2} className={inputClass} value={f.offlineMessage} onChange={(e) => set('offlineMessage', e.target.value)} />}</Field>
      <Field label="Colour">
        {(id) => (
          <span className="flex items-center gap-2">
            <input id={id} type="color" value={f.color} onChange={(e) => set('color', e.target.value)} className="h-9 w-12 rounded border border-line bg-transparent" />
            <span className="tabular text-sm text-ink-2">{f.color}</span>
          </span>
        )}
      </Field>
      <Toggle label="Widget on" checked={f.active} onChange={(v) => set('active', v)} />
    </Drawer>
  );
}
